import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import { toast } from 'react-toastify';

/* ─── types ────────────────────────────────────────────────── */

interface Props {
   storeName: string;
   shopUrl: string;
   logo: string;
   btnLabel: string;
   btnGradient: string;
   btnTextColor: string;
}

interface PortalType {
   key: string;
   name: string;
   desc: string;
   fileHint: string;
}

interface PortalTheme {
   key: string;
   name: string;
   bg: string;
   cardBg: string;
   accent: string;
}

/* ─── portal types ─────────────────────────────────────────── */

const PORTAL_TYPES: PortalType[] = [
   {
      key: 'mikrotik',
      name: 'MikroTik',
      desc: 'Default Hotspot',
      fileHint:
         'Save as login.html → upload via Winbox to Files → hotspot folder. Works with JuanFi and other MikroTik-based systems.',
   },
   {
      key: 'lpb',
      name: 'LPB',
      desc: 'Libre Piso WiFi',
      fileHint:
         'Replace the portal file on your LPB board. Access your LPB admin panel and upload this as the custom portal.',
   },
   {
      key: 'generic',
      name: 'Generic',
      desc: 'Any portal',
      fileHint:
         'Save as your portal login page (e.g. login.html) and upload to your vendo system.',
   },
];

/* ─── themes ───────────────────────────────────────────────── */

const PORTAL_THEMES: PortalTheme[] = [
   {
      key: 'gaming',
      name: 'Gaming',
      bg: 'linear-gradient(135deg, #0b1030, #1a0a3e)',
      cardBg: 'rgba(255,255,255,0.08)',
      accent: '#D2FD58',
   },
   {
      key: 'dark',
      name: 'Dark',
      bg: 'linear-gradient(135deg, #0f172a, #1e293b)',
      cardBg: 'rgba(255,255,255,0.06)',
      accent: '#60a5fa',
   },
   {
      key: 'clean',
      name: 'Clean',
      bg: 'linear-gradient(135deg, #f0f4ff, #e8ecf4)',
      cardBg: '#ffffff',
      accent: '#3760f9',
   },
];

/* ─── detection ────────────────────────────────────────────── */

const detectPortalType = (html: string): string => {
   const h = html.toLowerCase();
   if (h.includes('$(link-login-only)') || h.includes('sendin') || h.includes('mikrotik'))
      return 'mikrotik';
   if (h.includes('frmvoucher') || h.includes('hashcode01') || h.includes('$encoder'))
      return 'lpb';
   return 'generic';
};

/* ─── portal-specific form + script ────────────────────────── */

const portalForm: Record<string, string> = {
   mikrotik: `
<form name="sendin" action="$(link-login-only)" method="post" style="display:none">
  <input type="hidden" name="username"/>
  <input type="hidden" name="password"/>
  <input type="hidden" name="dst" value="$(link-orig)"/>
  <input type="hidden" name="popup" value="true"/>
</form>`,
   lpb: `
<form name="frmvoucher" id="frmvoucher" action="<?= $encoder ?>" method="post" style="display:none">
  <input type="hidden" name="hashcode01" id="hashcode01" value="<?= $hashcode01 ?>"/>
  <input type="hidden" name="vcode" id="sf-hidden-vcode"/>
</form>`,
   generic: `
<form id="sf-form" method="post" style="display:none">
  <input type="hidden" name="voucher" id="sf-hidden-voucher"/>
</form>`,
};

const portalScript: Record<string, string> = {
   mikrotik: `function sfConnect(){
  var c=document.getElementById('sf-voucher').value;
  if(!c){document.getElementById('sf-voucher').focus();return;}
  document.sendin.username.value=c;
  document.sendin.password.value=c;
  document.sendin.submit();
}`,
   lpb: `function sfConnect(){
  var c=document.getElementById('sf-voucher').value;
  if(!c){document.getElementById('sf-voucher').focus();return;}
  document.getElementById('sf-hidden-vcode').value=c;
  document.getElementById('frmvoucher').submit();
}`,
   generic: `function sfConnect(){
  var c=document.getElementById('sf-voucher').value;
  if(!c){document.getElementById('sf-voucher').focus();return;}
  document.getElementById('sf-hidden-voucher').value=c;
  document.getElementById('sf-form').submit();
}`,
};

/* ─── theme CSS blocks ─────────────────────────────────────── */

const themeCss: Record<string, string> = {
   gaming: `
:root{
  --bg:linear-gradient(135deg,#0b1030 0%,#1a0a3e 50%,#0b1030 100%);
  --card-bg:rgba(255,255,255,0.06);
  --card-border:rgba(210,253,88,0.12);
  --text:#fff;
  --text-sub:rgba(255,255,255,0.5);
  --accent:#D2FD58;
  --input-bg:rgba(255,255,255,0.06);
  --input-border:rgba(255,255,255,0.12);
  --input-focus:#D2FD58;
  --btn-bg:linear-gradient(135deg,#D2FD58,#a8e040);
  --btn-text:#0b1030;
  --divider:rgba(255,255,255,0.1);
  --glow:rgba(210,253,88,0.25);
}
body{background:var(--bg);}
.sf-wifi-wrap{filter:drop-shadow(0 0 12px var(--accent));}
.sf-input:focus{box-shadow:0 0 0 3px rgba(210,253,88,0.15);}
.sf-btn-connect{box-shadow:0 4px 20px var(--glow);}`,

   dark: `
:root{
  --bg:linear-gradient(135deg,#0f172a 0%,#1e293b 100%);
  --card-bg:rgba(255,255,255,0.05);
  --card-border:rgba(96,165,250,0.15);
  --text:#fff;
  --text-sub:rgba(255,255,255,0.45);
  --accent:#60a5fa;
  --input-bg:rgba(255,255,255,0.06);
  --input-border:rgba(255,255,255,0.12);
  --input-focus:#60a5fa;
  --btn-bg:linear-gradient(135deg,#3b82f6,#2563eb);
  --btn-text:#fff;
  --divider:rgba(255,255,255,0.08);
  --glow:rgba(59,130,246,0.2);
}
body{background:var(--bg);}
.sf-input:focus{box-shadow:0 0 0 3px rgba(59,130,246,0.15);}`,

   clean: `
:root{
  --bg:linear-gradient(135deg,#f0f4ff 0%,#e8ecf4 100%);
  --card-bg:#ffffff;
  --card-border:rgba(0,0,0,0.08);
  --text:#1a1a2e;
  --text-sub:#64748b;
  --accent:#3760f9;
  --input-bg:#f8fafc;
  --input-border:#e2e8f0;
  --input-focus:#3760f9;
  --btn-bg:linear-gradient(135deg,#3760f9,#2548e0);
  --btn-text:#fff;
  --divider:rgba(0,0,0,0.08);
  --glow:rgba(55,96,249,0.15);
}
body{background:var(--bg);}
.sf-card{box-shadow:0 8px 32px rgba(0,0,0,0.08);}
.sf-input:focus{box-shadow:0 0 0 3px rgba(55,96,249,0.12);}`,
};

/* ─── shared HTML pieces ───────────────────────────────────── */

const WIFI_ICON = `<div class="sf-wifi-wrap">
      <svg class="sf-wifi" width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path class="sf-w1" d="M2 8.82a15 15 0 0 1 20 0" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round"/>
        <path class="sf-w2" d="M5 12.55a11 11 0 0 1 14 0" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round"/>
        <path class="sf-w3" d="M8.53 16.11a6 6 0 0 1 6.95 0" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round"/>
        <circle cx="12" cy="20" r="1.5" fill="var(--accent)"/>
      </svg>
    </div>`;

const WIFI_BTN_SVG = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12.55a11 11 0 0 1 14 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1" fill="currentColor"/></svg>`;

const SHOP_BTN_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>`;

const COMMON_CSS = `
*{margin:0;padding:0;box-sizing:border-box;}
body{
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
  min-height:100vh;min-height:100dvh;
  display:flex;align-items:center;justify-content:center;
  color:var(--text);padding:20px;
  -webkit-font-smoothing:antialiased;
}
.sf-cna{
  display:none;position:fixed;top:0;left:0;right:0;z-index:999;
  background:#fef3c7;border-bottom:1px solid #f59e0b;color:#78350f;padding:12px 16px;
  text-align:center;font-size:13px;font-weight:500;
  box-shadow:0 2px 8px rgba(0,0,0,0.1);
}
.sf-cna-msg{margin:0 0 8px;font-weight:600;font-size:12px;}
.sf-cna button{
  background:#78350f;color:#fff;border:none;padding:7px 16px;
  border-radius:6px;font-size:11px;font-weight:700;cursor:pointer;
}
.sf-page{width:100%;max-width:380px;display:flex;flex-direction:column;align-items:center;}
.sf-card{
  width:100%;background:var(--card-bg);border:1px solid var(--card-border);
  border-radius:20px;padding:40px 28px 32px;text-align:center;
  backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);
}
.sf-logo{
  width:64px;height:64px;border-radius:16px;object-fit:cover;
  margin:0 auto 16px;display:block;
}
.sf-wifi-wrap{margin:0 auto 16px;text-align:center;}
@keyframes sf-pulse{0%,100%{opacity:.3}50%{opacity:1}}
.sf-w1{animation:sf-pulse 2s ease-in-out infinite;}
.sf-w2{animation:sf-pulse 2s ease-in-out .3s infinite;}
.sf-w3{animation:sf-pulse 2s ease-in-out .6s infinite;}
.sf-title{font-size:22px;font-weight:700;margin-bottom:4px;}
.sf-sub{font-size:14px;color:var(--text-sub);margin-bottom:24px;}
.sf-input{
  width:100%;padding:14px 16px;border-radius:12px;
  border:2px solid var(--input-border);background:var(--input-bg);
  color:var(--text);font-size:16px;text-align:center;outline:none;
  transition:border-color .2s,box-shadow .2s;
  letter-spacing:2px;font-weight:600;
}
.sf-input:focus{border-color:var(--input-focus);}
.sf-input::placeholder{color:var(--text-sub);letter-spacing:0;font-weight:400;}
.sf-btn-connect{
  width:100%;padding:14px;border-radius:12px;border:none;
  background:var(--btn-bg);color:var(--btn-text);
  font-size:15px;font-weight:700;cursor:pointer;margin-top:12px;
  display:flex;align-items:center;justify-content:center;gap:8px;
  transition:opacity .2s,transform .1s;
}
.sf-btn-connect:active{transform:scale(.98);}
.sf-divider{
  display:flex;align-items:center;margin:20px 0;
  color:var(--text-sub);font-size:12px;
}
.sf-divider::before,.sf-divider::after{content:'';flex:1;height:1px;background:var(--divider);}
.sf-divider span{padding:0 12px;white-space:nowrap;}
.sf-shop-btn{
  display:flex;width:100%;padding:12px;border-radius:10px;border:none;
  font-size:14px;font-weight:600;cursor:pointer;
  align-items:center;justify-content:center;gap:8px;
  text-decoration:none;
  box-shadow:0 4px 14px rgba(0,0,0,0.15);
  transition:opacity .2s;letter-spacing:.02em;
}
.sf-shop-btn:active{opacity:.85;}
.sf-error{
  background:rgba(220,53,69,0.12);color:#dc3545;
  padding:8px 12px;border-radius:8px;font-size:13px;
  font-weight:500;margin-top:10px;
}
.sf-trial{
  margin-top:16px;padding:12px;border-radius:10px;
  background:var(--input-bg);border:1px solid var(--divider);
  font-size:13px;color:var(--text-sub);
}
.sf-trial a{
  color:var(--accent);font-weight:700;text-decoration:underline;
  font-size:15px;
}
.sf-footer{margin-top:20px;font-size:12px;color:var(--text-sub);text-align:center;}
.sf-footer a{color:var(--accent);text-decoration:none;font-weight:600;}`;

const CNA_SCRIPT = `
/* --- Captive portal browser detection (iOS CNA + Android WebView) --- */
(function(){
  var ua=navigator.userAgent;
  var iOS=/iPhone|iPad|iPod/.test(ua);
  var android=/Android/.test(ua);
  var isCNA=iOS&&!('serviceWorker' in navigator);
  var isWebView=android&&(/wv\\)/.test(ua)||/CaptivePortal/.test(ua));
  if(!isCNA&&!isWebView)return;

  var banner=document.getElementById('sf-cna');
  var msg=document.getElementById('sf-cna-msg');
  var btn=document.getElementById('sf-cna-btn');
  if(!banner)return;

  if(isWebView){
    msg.textContent='Open in Chrome for GCash/Maya payments';
    btn.textContent='Open in Chrome';
    btn.onclick=function(){
      var url=window.location.href.replace(/^https?:\\/\\//,'');
      window.location.href='intent://'+url+'#Intent;scheme=https;package=com.android.chrome;end';
    };
  } else {
    msg.textContent='Open in Safari for GCash/Maya payments';
    btn.textContent='Copy Link';
    btn.onclick=function(){
      if(navigator.clipboard){
        navigator.clipboard.writeText(window.location.href);
      } else {
        var i=document.createElement('input');i.value=window.location.href;
        document.body.appendChild(i);i.select();document.execCommand('copy');
        document.body.removeChild(i);
      }
      btn.textContent='Copied! Open Safari';
    };
  }

  banner.style.display='block';
  document.body.style.paddingTop='60px';

  var a=document.getElementById('sf-shop-link');
  if(a&&isCNA){
    a.removeAttribute('target');
    a.onclick=function(e){e.preventDefault();window.location.href=a.href;};
  }
})();`;

/* ─── HTML generators ──────────────────────────────────────── */

interface GeneratorConfig {
   portalType: string;
   theme: string;
   storeName: string;
   shopUrl: string;
   logo: string;
   btnLabel: string;
   btnGradient: string;
   btnTextColor: string;
}

const generateMikrotikPortal = (cfg: GeneratorConfig): string => {
   const logoHtml = cfg.logo
      ? `<img src="${cfg.logo}" class="sf-logo" alt="${cfg.storeName}"/>`
      : WIFI_ICON;

   return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta http-equiv="pragma" content="no-cache"/>
<meta http-equiv="expires" content="-1"/>
<meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no">
<title>${cfg.storeName} - WiFi</title>
<style>
${themeCss[cfg.theme] || themeCss.gaming}
${COMMON_CSS}
</style>
</head>
<body>

<!-- MikroTik CHAP authentication -->
\$(if chap-id)
<form name="sendin" action="\$(link-login-only)" method="post" style="display:none">
  <input type="hidden" name="username"/>
  <input type="hidden" name="password"/>
  <input type="hidden" name="dst" value="\$(link-orig)"/>
  <input type="hidden" name="popup" value="true"/>
</form>
<script src="/md5.js"></script>
<script>
function doLogin(){
  document.sendin.username.value=document.login.username.value;
  document.sendin.password.value=hexMD5('\$(chap-id)'+document.login.password.value+'\$(chap-challenge)');
  document.sendin.submit();
  return false;
}
</script>
\$(endif)

<!-- Captive portal browser banner (iOS CNA / Android WebView) -->
<div id="sf-cna" class="sf-cna">
  <p id="sf-cna-msg" class="sf-cna-msg"></p>
  <button id="sf-cna-btn"></button>
</div>

<div class="sf-page">
  <div class="sf-card">
    ${logoHtml}
    <h1 class="sf-title">${cfg.storeName}</h1>
    <p class="sf-sub">Enter your voucher code to connect</p>

    <!-- MikroTik login form — username IS the voucher code -->
    <form name="login" action="\$(link-login-only)" method="post"
          \$(if chap-id) onSubmit="return doLogin()" \$(endif)>
      <input type="hidden" name="dst" value="\$(link-orig)"/>
      <input type="hidden" name="popup" value="true"/>

      <input name="username" id="sf-voucher" class="sf-input"
             type="text" inputmode="numeric" value="\$(username)"
             placeholder="Voucher Code" autocomplete="off" autofocus/>
      <input name="password" type="hidden"/>

      <button type="submit" class="sf-btn-connect">
        ${WIFI_BTN_SVG} Connect
      </button>
    </form>

    <!-- MikroTik error display -->
    \$(if error)<div class="sf-error">\$(error)</div>\$(endif)

    <div class="sf-divider"><span>or buy a voucher</span></div>

    <a href="${cfg.shopUrl}" rel="noopener" id="sf-shop-link"
       class="sf-shop-btn"
       onclick="window.location.href='${cfg.shopUrl}';return false;"
       style="background:${cfg.btnGradient};color:${cfg.btnTextColor};">
      ${SHOP_BTN_SVG} ${cfg.btnLabel}
    </a>

    <!-- MikroTik free trial -->
    \$(if trial == 'yes')
    <div class="sf-trial">
      Free Internet Trial:
      <a href="\$(link-login-only)?dst=\$(link-orig-esc)&amp;username=T-\$(mac-esc)">Click Here</a>
    </div>
    \$(endif)
  </div>
  <p class="sf-footer">Powered by <a href="https://smartfi.ph">SmartFi</a></p>
</div>

<script>
/* --- auto-fill from ?voucher= (from SmartFi shop success page) --- */
(function(){
  var p=new URLSearchParams(location.search),v=p.get('voucher');
  if(!v)return;
  var el=document.getElementById('sf-voucher');
  if(el){
    el.value=v;
    /* Auto-submit after short delay */
    setTimeout(function(){
      var f=document.forms['login'];
      if(f){
        /* Trigger CHAP if available, otherwise direct submit */
        if(typeof doLogin==='function'){doLogin();}
        else{f.submit();}
      }
    },500);
  }
})();
${CNA_SCRIPT}
</script>
</body>
</html>`;
};

const generateGenericPortal = (cfg: GeneratorConfig): string => {
   const logoHtml = cfg.logo
      ? `<img src="${cfg.logo}" class="sf-logo" alt="${cfg.storeName}"/>`
      : WIFI_ICON;

   return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1,user-scalable=no">
<title>${cfg.storeName} - WiFi</title>
<style>
${themeCss[cfg.theme] || themeCss.gaming}
${COMMON_CSS}
</style>
</head>
<body>

<!-- Captive portal browser banner (iOS CNA / Android WebView) -->
<div id="sf-cna" class="sf-cna">
  <p id="sf-cna-msg" class="sf-cna-msg"></p>
  <button id="sf-cna-btn"></button>
</div>

<div class="sf-page">
  <div class="sf-card">
    ${logoHtml}
    <h1 class="sf-title">${cfg.storeName}</h1>
    <p class="sf-sub">Enter your voucher code to connect</p>

    <input type="text" id="sf-voucher" class="sf-input"
           placeholder="Voucher Code" autocomplete="off" autofocus
           onkeydown="if(event.key==='Enter')sfConnect()"/>

    <button class="sf-btn-connect" onclick="sfConnect()">
      ${WIFI_BTN_SVG} Connect
    </button>

    <div class="sf-divider"><span>or buy a voucher</span></div>

    <a href="${cfg.shopUrl}" rel="noopener" id="sf-shop-link"
       class="sf-shop-btn"
       onclick="window.location.href='${cfg.shopUrl}';return false;"
       style="background:${cfg.btnGradient};color:${cfg.btnTextColor};">
      ${SHOP_BTN_SVG} ${cfg.btnLabel}
    </a>
  </div>
  <p class="sf-footer">Powered by <a href="https://smartfi.ph">SmartFi</a></p>
</div>

${portalForm[cfg.portalType] || portalForm.generic}

<script>
${portalScript[cfg.portalType] || portalScript.generic}

/* --- auto-fill from ?voucher= --- */
(function(){
  var p=new URLSearchParams(location.search),v=p.get('voucher');
  if(!v)return;
  var el=document.getElementById('sf-voucher');
  if(el){el.value=v;setTimeout(sfConnect,500);}
})();
${CNA_SCRIPT}
</script>
</body>
</html>`;
};

const generatePortalHtml = (cfg: GeneratorConfig): string => {
   if (cfg.portalType === 'mikrotik') return generateMikrotikPortal(cfg);
   return generateGenericPortal(cfg);
};

/* ─── component ────────────────────────────────────────────── */

const PortalGenerator = ({
   storeName,
   shopUrl,
   logo,
   btnLabel,
   btnGradient,
   btnTextColor,
}: Props) => {
   const [portalType, setPortalType] = useState('mikrotik');
   const [theme, setTheme] = useState('gaming');
   const [pastedHtml, setPastedHtml] = useState('');
   const [showPaste, setShowPaste] = useState(false);
   const [generatedHtml, setGeneratedHtml] = useState('');

   const handlePaste = (val: string) => {
      setPastedHtml(val);
      if (val.trim()) {
         const detected = detectPortalType(val);
         setPortalType(detected);
         const name = PORTAL_TYPES.find((t) => t.key === detected)?.name || detected;
         toast.info(`Detected portal: ${name}`, { autoClose: 2000 });
      }
   };

   const handleGenerate = () => {
      if (!storeName || !shopUrl) {
         toast.error('Store name and shop URL are required');
         return;
      }
      const html = generatePortalHtml({
         portalType,
         theme,
         storeName,
         shopUrl,
         logo,
         btnLabel: btnLabel || 'Pay via Gcash',
         btnGradient,
         btnTextColor,
      });
      setGeneratedHtml(html);
      toast.success('Portal generated!', { autoClose: 2000 });
   };

   const handleCopy = (val: string) => {
      navigator.clipboard
         .writeText(val)
         .then(() => toast.success('Copied to clipboard', { autoClose: 1500 }))
         .catch(() => toast.error('Failed to copy'));
   };

   const handleDownload = (val: string) => {
      const ext = portalType === 'lpb' ? 'php' : 'html';
      const filename = portalType === 'lpb' ? 'portal.php' : 'login.html';
      const blob = new Blob([val], { type: `text/${ext}` });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`, { autoClose: 1500 });
   };

   const currentType = PORTAL_TYPES.find((t) => t.key === portalType);

   return (
      <Wrapper>
         {/* Portal Type */}
         <Section>
            <SectionLabel>Portal Type</SectionLabel>
            <TypeGrid>
               {PORTAL_TYPES.map((t) => (
                  <TypeCard
                     key={t.key}
                     $active={portalType === t.key}
                     onClick={() => setPortalType(t.key)}
                  >
                     <TypeName>{t.name}</TypeName>
                     <TypeDesc>{t.desc}</TypeDesc>
                  </TypeCard>
               ))}
            </TypeGrid>
         </Section>

         {/* Theme */}
         <Section>
            <SectionLabel>Theme</SectionLabel>
            <ThemeGrid>
               {PORTAL_THEMES.map((t) => (
                  <ThemeCard
                     key={t.key}
                     $active={theme === t.key}
                     onClick={() => setTheme(t.key)}
                  >
                     <ThemePreview $bg={t.bg}>
                        <ThemeMockCard $cardBg={t.cardBg}>
                           <ThemeMockDot $color={t.accent} />
                           <ThemeMockBar $color={t.accent} $width="60%" />
                           <ThemeMockBar $color={t.cardBg} $width="80%" style={{ opacity: 0.5 }} />
                           <ThemeMockBtn $color={t.accent} />
                        </ThemeMockCard>
                     </ThemePreview>
                     <ThemeName>{t.name}</ThemeName>
                  </ThemeCard>
               ))}
            </ThemeGrid>
         </Section>

         {/* Paste HTML (optional) */}
         <Section>
            <PasteToggle onClick={() => setShowPaste(!showPaste)}>
               <PasteToggleIcon>{showPaste ? '▾' : '▸'}</PasteToggleIcon>
               Paste your current portal HTML (auto-detect type)
            </PasteToggle>
            {showPaste && (
               <PasteArea
                  value={pastedHtml}
                  onChange={(e) => handlePaste(e.target.value)}
                  placeholder="Paste your portal HTML here to auto-detect the portal type..."
                  rows={6}
               />
            )}
         </Section>

         {/* Generate */}
         <GenerateBtn onClick={handleGenerate}>Generate Portal</GenerateBtn>

         {/* Output */}
         {generatedHtml && (
            <Section>
               <InstructionBox>
                  <InstructionTitle>How to install</InstructionTitle>
                  <InstructionText>{currentType?.fileHint}</InstructionText>
               </InstructionBox>

               <CodeEditorWrapper>
                  <CodeEditorHead>
                     <CodeEditorHeadTitle>Generated Portal HTML</CodeEditorHeadTitle>
                     <CodeEditorHeadActions>
                        <CodeEditorHeadLabel onClick={() => handleDownload(generatedHtml)}>
                           <span>Download</span>
                           <DownloadIcon>&#8595;</DownloadIcon>
                        </CodeEditorHeadLabel>
                        <CodeEditorHeadLabel onClick={() => handleCopy(generatedHtml)}>
                           <span>Copy HTML</span>
                           <IconsLib icon="file-copy-line" size="15px" color="white" />
                        </CodeEditorHeadLabel>
                     </CodeEditorHeadActions>
                  </CodeEditorHead>
                  <CodeEditorBody>
                     <SyntaxHighlighter language="html" style={coy}>
                        {generatedHtml}
                     </SyntaxHighlighter>
                  </CodeEditorBody>
               </CodeEditorWrapper>
            </Section>
         )}
      </Wrapper>
   );
};

/* ─── styles ───────────────────────────────────────────────── */

const Wrapper = styled.div``;

const Section = styled.div`
   margin-bottom: 16px;
`;

const SectionLabel = styled.p`
   font-size: 14px;
   font-weight: 600;
   color: ${colors.GRAY1};
   margin-bottom: 8px;
`;

const TypeGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 8px;
`;

const TypeCard = styled.div<{ $active: boolean }>`
   border: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 10px;
   padding: 12px 8px;
   cursor: pointer;
   text-align: center;
   transition: all 0.15s;
   background: ${({ $active }) => ($active ? `${colors.PRIMARY}08` : 'transparent')};
   &:hover {
      border-color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY10)};
   }
`;

const TypeName = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY1};
`;

const TypeDesc = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const ThemeGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 8px;
`;

const ThemeCard = styled.div<{ $active: boolean }>`
   border: 2px solid ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 10px;
   padding: 6px;
   cursor: pointer;
   transition: all 0.15s;
   background: ${({ $active }) => ($active ? `${colors.PRIMARY}08` : 'transparent')};
   &:hover {
      border-color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY10)};
   }
`;

const ThemePreview = styled.div<{ $bg: string }>`
   background: ${({ $bg }) => $bg};
   border-radius: 6px;
   height: 80px;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 8px;
`;

const ThemeMockCard = styled.div<{ $cardBg: string }>`
   background: ${({ $cardBg }) => $cardBg};
   border-radius: 6px;
   padding: 8px 10px;
   width: 100%;
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 4px;
`;

const ThemeMockDot = styled.div<{ $color: string }>`
   width: 10px;
   height: 10px;
   border-radius: 50%;
   background: ${({ $color }) => $color};
   margin-bottom: 2px;
`;

const ThemeMockBar = styled.div<{ $color: string; $width: string }>`
   width: ${({ $width }) => $width};
   height: 3px;
   border-radius: 2px;
   background: ${({ $color }) => $color};
`;

const ThemeMockBtn = styled.div<{ $color: string }>`
   width: 70%;
   height: 6px;
   border-radius: 3px;
   background: ${({ $color }) => $color};
   margin-top: 2px;
`;

const ThemeName = styled.p`
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-align: center;
   margin-top: 5px;
`;

const PasteToggle = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 13px;
   color: ${colors.GRAY3};
   cursor: pointer;
   user-select: none;
   &:hover {
      color: ${colors.PRIMARY};
   }
`;

const PasteToggleIcon = styled.span`
   font-size: 10px;
   width: 12px;
`;

const PasteArea = styled.textarea`
   width: 100%;
   margin-top: 8px;
   padding: 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: ${colors.GRAY14};
   color: ${colors.GRAY1};
   font-size: 12px;
   font-family: monospace;
   resize: vertical;
   outline: none;
   &:focus {
      border-color: ${colors.PRIMARY};
   }
`;

const GenerateBtn = styled.button`
   width: 100%;
   padding: 12px;
   border-radius: 10px;
   border: none;
   background: ${colors.PRIMARY};
   color: white;
   font-size: 14px;
   font-weight: 700;
   cursor: pointer;
   margin-bottom: 16px;
   transition: opacity 0.2s;
   &:hover {
      opacity: 0.9;
   }
   &:active {
      transform: scale(0.99);
   }
`;

const InstructionBox = styled.div`
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 12px 14px;
   margin-bottom: 10px;
`;

const InstructionTitle = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.GRAY1};
   margin-bottom: 4px;
`;

const InstructionText = styled.p`
   font-size: 12px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;

const CodeEditorWrapper = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 4px;
`;

const CodeEditorHead = styled.div`
   background-color: ${colors.GRAY4};
   display: flex;
   align-items: center;
   justify-content: space-between;
   border-top-left-radius: 4px;
   border-top-right-radius: 4px;
`;

const CodeEditorHeadActions = styled.div`
   display: flex;
   align-items: center;
   gap: 4px;
`;

const CodeEditorHeadTitle = styled.p`
   font-size: 12px;
   color: white;
   padding: 2px 10px;
`;

const CodeEditorHeadLabel = styled.div`
   font-size: 12px;
   color: white;
   display: flex;
   gap: 4px;
   margin-right: 10px;
   padding: 2px 5px;
   border-radius: 4px;
   cursor: pointer;
   &:hover {
      color: ${colors.GRAY13};
      background-color: ${colors.GRAY2};
   }
`;

const CodeEditorBody = styled.div`
   font-size: 14px;
   pre {
      margin: 0 !important;
   }
   code {
      background: ${colors.GRAY14} !important;
      white-space: pre-wrap !important;
      word-break: break-word !important;
      max-height: 200px !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
      color: ${colors.PRIMARY} !important;
      &::-webkit-scrollbar {
         width: 7px;
      }
      &::-webkit-scrollbar-track {
         border-radius: 10px;
      }
      &::-webkit-scrollbar-thumb {
         background: ${colors.GRAY11};
         border-radius: 10px;
      }
      &::-webkit-scrollbar-thumb:hover {
         background: ${colors.GRAY10};
      }
   }
`;

const DownloadIcon = styled.span`
   font-size: 15px;
   line-height: 1;
`;

export default PortalGenerator;
